import type { ConfiguracaoOficinaCorte, FerramentaOficinaCorte, IdZonaOperacaoPorta, OperacaoPorta } from './oficinaCorte'
import type { IdZonaOperacaoTampa, OperacaoTampa } from './oficinaPortaMalas'
import type { IdZonaOperacaoTerceiraPorta, OperacaoTerceiraPorta } from './oficinaTerceiraPorta'
import type { IdZonaRebatimentoPainel, OperacaoRebatimentoPainel } from './rebatimentoPainel'
import type { IdZonaElevacaoPainel, OperacaoElevacaoPainel } from './elevacaoPainel'
import type { IdZonaTetoLateral, OperacaoTetoLateral } from './rebatimentoLateralTeto'
import type { ModoAlargador } from './interacaoOficina'

export type IdTecnicaCorte =
  | 'portaDobradicas'
  | 'portaParalama'
  | 'portaTrava'
  | 'portaMacaneta'
  | 'portaEsquadria'
  | 'rebatimentoPainel'
  | 'elevacaoPainel'
  | 'rebatimentoLateralTeto'
  | 'terceiraPorta'
  | 'portaMalas'

export type CategoriaEtapaCorte =
  | 'vidro'
  | 'abertura'
  | 'rompimento'
  | 'vinculos'
  | 'sustentacao'
  | 'remocao'

/** Ritmo de apresentação, em milissegundos na velocidade 1×. */
export interface PerfilAnimacaoEtapa {
  readonly enquadrandoMs: number
  readonly aproximandoMs: number
  readonly contatoMs: number
  readonly executandoMs: number
  readonly recuandoMs: number
  readonly observandoMs: number
}

const PERFIL_FERRAMENTA: PerfilAnimacaoEtapa = {
  enquadrandoMs: 520,
  aproximandoMs: 820,
  contatoMs: 280,
  executandoMs: 1800,
  recuandoMs: 650,
  observandoMs: 850,
}

const PERFIL_ACAO_EQUIPE: PerfilAnimacaoEtapa = {
  enquadrandoMs: 520,
  aproximandoMs: 0,
  contatoMs: 0,
  executandoMs: 900,
  recuandoMs: 0,
  observandoMs: 950,
}

/** Como as garras chegam à peça antes do contato. */
export type EstadoInicialFerramenta = 'aberto' | 'fechado'

/**
 * Padrão de cada ferramenta de garra: o alargador entra fechado e o cortador
 * aberto. Um passo pode pedir o contrário com `estadoInicial`.
 */
const ESTADO_INICIAL_PADRAO: Partial<Record<FerramentaOficinaCorte, EstadoInicialFerramenta>> = {
  alargador: 'fechado',
  cortadorHidraulico: 'aberto',
}

export interface EtapaDemonstracaoCorte {
  readonly id: string
  readonly titulo: string
  readonly categoria: CategoriaEtapaCorte
  readonly ferramenta: FerramentaOficinaCorte | 'nenhuma'
  readonly modoAlargador?: ModoAlargador
  /** Sobrepõe o estado inicial padrão da ferramenta (ver `estadoInicialDaEtapa`). */
  readonly estadoInicial?: EstadoInicialFerramenta
  /** Zona 3D onde a ferramenta se posiciona no veículo. */
  readonly zonaAlvo: IdZonaOperacaoPorta | IdZonaOperacaoTampa | IdZonaOperacaoTerceiraPorta | IdZonaRebatimentoPainel | IdZonaElevacaoPainel | IdZonaTetoLateral | null
  /** Operação de domínio acionada ao término da etapa. */
  readonly operacaoPainel?: OperacaoRebatimentoPainel
  readonly operacaoElevacao?: OperacaoElevacaoPainel
  readonly operacaoTetoLateral?: OperacaoTetoLateral
  readonly operacaoPorta?: OperacaoPorta
  readonly operacaoTampa?: OperacaoTampa
  readonly operacaoTerceiraPorta?: OperacaoTerceiraPorta
  /** Se a etapa executa manejo de vidro previamente à deformação estrutural. */
  readonly requerManejoVidro?: boolean
  readonly explicacao: string
  readonly riscos: string
  readonly dicaSeguranca: string
  /** Ajuste opcional sobre o ritmo padrão da ferramenta ou ação da equipe. */
  readonly animacao?: Partial<PerfilAnimacaoEtapa>
}

export function estadoInicialDaEtapa(etapa: EtapaDemonstracaoCorte): EstadoInicialFerramenta | null {
  if (etapa.ferramenta === 'nenhuma') return null
  return etapa.estadoInicial ?? ESTADO_INICIAL_PADRAO[etapa.ferramenta] ?? null
}

export function perfilAnimacaoDaEtapa(etapa: EtapaDemonstracaoCorte): PerfilAnimacaoEtapa {
  return { ...(etapa.ferramenta === 'nenhuma' ? PERFIL_ACAO_EQUIPE : PERFIL_FERRAMENTA), ...etapa.animacao }
}

export interface TecnicaCorte {
  readonly id: IdTecnicaCorte
  readonly nome: string
  readonly subtitulo: string
  readonly descricao: string
  readonly peca: 'porta' | 'tampaTraseira' | 'terceiraPorta'
  readonly configuracao: ConfiguracaoOficinaCorte
  readonly complexidade: 'Básica' | 'Intermediária' | 'Avançada'
  readonly tempoEstimadoMinutos: number
  readonly etapas: readonly EtapaDemonstracaoCorte[]
}

function retiradaViaAcesso(tecnicaBase: TecnicaCorte, id: 'portaMacaneta' | 'portaEsquadria', acesso: EtapaDemonstracaoCorte): TecnicaCorte {
  const etapa = (idEtapa: string) => tecnicaBase.etapas.find((e) => e.id === idEtapa)!
  return {
    ...tecnicaBase,
    id,
    nome: id === 'portaMacaneta' ? 'Retirada de Porta — Acesso pela Maçaneta' : 'Retirada de Porta — Acesso pela Esquadria',
    subtitulo: id === 'portaMacaneta' ? 'Compressão da maçaneta com o alargador' : 'Uma ponta na esquadria superior e outra na inferior',
    descricao: `${acesso.explicacao} Depois, expande-se no pino da trava, liberam-se as dobradiças e os vínculos residuais e retira-se a porta sustentada.`,
    etapas: [etapa('vidro'), acesso, etapa('sustentacao'), etapa('expandirTrava'), etapa('corteDobradicaSup'), etapa('corteDobradicaInf'), etapa('remocao')],
  }
}

const TECNICAS_BASE: readonly TecnicaCorte[] = [
  {
    id: 'portaDobradicas',
    nome: 'Retirada de Porta — Via Dobradiças',
    subtitulo: 'Técnica com criação de espaço, rompimento da fechadura e expansão das dobradiças com alargador',
    descricao:
      'Método principal de acesso lateral. Cria-se o gap com a Barra Halligan, estoura-se a fechadura com o alargador, abre-se a porta manualmente para expor as dobradiças na coluna A, estoura-se cada dobradiça com o alargador e remove-se a porta sob sustentação controlada.',
    peca: 'porta',
    configuracao: 'quatroRodas',
    complexidade: 'Básica',
    tempoEstimadoMinutos: 4,
    etapas: [
      {
        id: 'vidro',
        titulo: 'Manejo e quebra de vidro com Punção Holmatro',
        categoria: 'vidro',
        ferramenta: 'puncaoVidro',
        zonaAlvo: 'esquadria',
        requerManejoVidro: true,
        explicacao:
          'Apoia-se a ponta de carbeto do punção no canto inferior do vidro temperado. O operador puxa a empunhadura tensionando a mola interna e solta: o impacto mecânico desestabiliza a tensão superficial e estilhaça o vidro controladamente.',
        riscos: 'Projeção de microestilhaços de vidro sobre a vítima e socorristas.',
        dicaSeguranca: 'Posicionar a Proteção rígida (Lágrima) entre a vítima e o vidro, cobrir com cobertor e utilizar EPI facial completo.',
      },
      {
        id: 'pontoApoio',
        titulo: 'Criação do ponto de apoio (gap) na fechadura com Barra Halligan',
        categoria: 'abertura',
        ferramenta: 'hooligan',
        zonaAlvo: 'trava',
        operacaoPorta: 'criarAbertura',
        explicacao:
          'A ponta (cunha) da Barra Halligan entra na fresta próximo à fechadura da porta, encaixa com precisão entre o batente e gira. A torção afasta a chapa e abre o vão inicial (gap de 10 a 15 cm) para a entrada das pontas do alargador.',
        riscos: 'Escorregamento da ferramenta com perda de apoio; esmagamento de dedos.',
        dicaSeguranca: 'Mantenha o corpo posicionado fora do raio de alavanca e apoie firmemente contra a coluna.',
      },
      {
        id: 'trava',
        titulo: 'Estourar a fechadura com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'trava',
        operacaoPorta: 'expandirTrava',
        explicacao:
          'As pontas do alargador entram fechadas no gap criado pela Halligan, entre a porta e a coluna B na altura da fechadura; ao expandir, a lingueta/pino da trava estoura e a porta se solta do batente.',
        riscos: 'Salto súbito da porta para fora com a liberação da trava sob pressão.',
        dicaSeguranca: 'O operador deve se posicionar fora do curso de abertura da porta.',
      },
      {
        id: 'aberturaManual',
        titulo: 'Abertura manual da porta para expor as dobradiças',
        categoria: 'abertura',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'testarAberturaManual',
        explicacao:
          'Com a trava estourada, a equipe puxa a porta com as mãos e a abre até o fim do curso, deixando as dobradiças da coluna A acessíveis para o alargador.',
        riscos: 'Porta presa por deformação residual voltando de forma brusca.',
        dicaSeguranca: 'Puxar pela borda traseira com luvas de resgate, mantendo as mãos fora da linha das dobradiças.',
      },
      {
        id: 'dobradicaSuperior',
        titulo: 'Estourar a dobradiça superior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaSuperior',
        operacaoPorta: 'expandirDobradiçaSuperior',
        explicacao:
          'Com a porta aberta manualmente, as pontas do alargador entram no vão da dobradiça exposta. Ao expandir, a força de separação estoura o pino e os parafusos de fixação na coluna A.',
        riscos: 'Projeção de fragmentos de parafusos de aço de alta resistência no momento do rompimento.',
        dicaSeguranca: 'Posicione as pontas profundamente no vão e mantenha a ferramenta apoiada com firmeza.',
      },
      {
        id: 'dobradicaInferior',
        titulo: 'Estourar a dobradiça inferior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaInferior',
        operacaoPorta: 'expandirDobradiçaInferior',
        explicacao:
          'Com a porta aberta e a dobradiça superior rompida, o alargador é inserido na dobradiça inferior, último vínculo estrutural da coluna A. A expansão mecânica estoura a fixação enquanto o socorrista auxiliar já sustenta a porta.',
        riscos: 'Movimento brusco de pêndulo ou torção da porta com impacto na coluna A.',
        dicaSeguranca: 'O operador auxiliar já deve se posicionar próximo à extremidade livre da porta para apoiar.',
      },
      {
        id: 'sustentacao',
        titulo: 'Sustentação física da porta',
        categoria: 'sustentacao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'sustentarPorta',
        explicacao:
          'Dois socorristas assumem o peso e o direcionamento da porta, garantindo que o destravamento final não resulte em queda da peça sobre a vítima ou socorristas. A porta é forçada além do curso, o que rompe o limitador, e o chicote elétrico é desconectado à mão.',
        riscos: 'Queda descontrolada de peça pesada sobre membros inferiores ou na rota de extração.',
        dicaSeguranca: 'Pegada firme em áreas limpas da chapa, utilizando luvas de proteção contra cortes.',
      },
      {
        id: 'remocaoFinal',
        titulo: 'Retirada da porta e descarte na área de entulho',
        categoria: 'remocao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'removerPorta',
        explicacao:
          'A porta é desengatada do pino da trava e conduzida para a área designada de descarte de ferragens da cena. O vão lateral fica completamente liberado para a rota de extração.',
        riscos: 'Bordas cortantes expostas na soleira e colunas.',
        dicaSeguranca: 'Instale mantas de proteção de borda cortante nas quinas vivas remanescentes da carroceria.',
      },
    ],
  },
  {
    id: 'portaParalama',
    nome: 'Retirada de Porta — Acesso pelo Para-lama',
    subtitulo: 'Técnica de alívio rápido quando a fresta da porta está deformada ou obstruída',
    descricao:
      'Quando o impacto frontal ou lateral prende a porta sem folga para ferramentas manuais, comprime-se o para-lama dianteiro com o alargador para expor diretamente as dobradiças.',
    peca: 'porta',
    configuracao: 'quatroRodas',
    complexidade: 'Intermediária',
    tempoEstimadoMinutos: 4,
    etapas: [
      {
        id: 'vidro',
        titulo: 'Manejo do vidro com Punção Holmatro',
        categoria: 'vidro',
        ferramenta: 'puncaoVidro',
        zonaAlvo: 'esquadria',
        requerManejoVidro: true,
        explicacao:
          'Aplicação do punção com mola no vértice inferior da janela da porta, quebrando o vidro antes de forçar o para-lama dianteiro.',
        riscos: 'Quebra espontânea sob estresse mecânico se não for quebrado preventivamente.',
        dicaSeguranca: 'Posicionar a Proteção rígida (Lágrima) entre a vítima e a janela para reter estilhaços no impacto.',
      },
      {
        id: 'amassarParalama',
        titulo: 'Pinçamento e deformação do para-lama com alargador',
        categoria: 'abertura',
        ferramenta: 'alargador',
        modoAlargador: 'fechar',
        zonaAlvo: 'paralama',
        operacaoPorta: 'amassarParalama',
        explicacao:
          'O operador entra de frente para o veículo (posicionado à frente da roda dianteira) e apoia o alargador acima do meio da roda, com uma ponta na crista superior do para-lama (junto ao capô) e a outra no arco da caixa de roda. O fechamento comprime e esmaga o centro do para-lama e em seguida a ferramenta alavanca a chapa para a frente, provocando o encolhimento longitudinal da lataria e expondo o vão de acesso direto às dobradiças na Coluna A.',
        riscos: 'Rompimento da chapa com deslocamento rápido de material metálico sob pressão.',
        dicaSeguranca: 'Mantenha os braços estendidos e corpo posicionado fora da linha de alavanca da ferramenta.',
      },
      {
        id: 'expansaoDobradicaSup',
        titulo: 'Estourar a dobradiça superior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaSuperior',
        operacaoPorta: 'expandirDobradiçaSuperior',
        explicacao:
          'Com o para-lama afastado, as pontas do alargador entram diretamente no vão exposto da dobradiça superior. Ao expandir no modo abrir, a força mecânica rompe e estoura os parafusos e o pino de fixação na Coluna A.',
        riscos: 'Ejeção de parafusos rompidos sob tração extrema.',
        dicaSeguranca: 'Todos os operadores devem manter viseiras abaixadas e ferramentas auxiliares sob controle.',
      },
      {
        id: 'expansaoDobradicaInf',
        titulo: 'Estourar a dobradiça inferior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaInferior',
        operacaoPorta: 'expandirDobradiçaInferior',
        explicacao:
          'O alargador é posicionado no vão inferior da porta. Ao expandir no modo abrir, estoura a dobradiça inferior, liberando completamente a fixação dianteira da porta.',
        riscos: 'Porta solta balançando para fora após romper os vínculos dianteiros.',
        dicaSeguranca: 'Coordenação estreita com o socorrista de apoio para conter qualquer deslocamento brusco da porta.',
      },
      {
        id: 'trava',
        titulo: 'Liberação da fechadura com alargador',
        categoria: 'vinculos',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'trava',
        operacaoPorta: 'expandirTrava',
        explicacao:
          'Com as dobradiças dianteiras estouradas e sustentadas pela equipe, o alargador expande no mecanismo da fechadura (coluna B), desengatando o pino da trava e liberando a porta.',
        riscos: 'Movimento súbito da porta ao soltar o último ponto de ancoragem mecânica.',
        dicaSeguranca: 'Mantenha a sustentação manual coordenada antes do destravamento final da fechadura.',
      },
      {
        id: 'sustentacao',
        titulo: 'Sustentação da porta',
        categoria: 'sustentacao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'sustentarPorta',
        explicacao: 'Apoio da peça por socorrista auxiliar. Forçada além do curso, a porta rompe o limitador; o chicote elétrico é desconectado à mão.',
        riscos: 'Perda de equilíbrio ao manusear a porta.',
        dicaSeguranca: 'Mantenha base estável com pernas afastadas ao suportar a carga.',
      },
      {
        id: 'remocao',
        titulo: 'Retirada e liberação do habitáculo',
        categoria: 'remocao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'removerPorta',
        explicacao: 'Conclusão da remoção e depósito na área segura.',
        riscos: 'Ferimentos na manipulação de chapa vincada.',
        dicaSeguranca: 'Transporte com empunhadura firme e deposite fora das passagens de socorristas.',
      },
    ],
  },
  {
    id: 'portaTrava',
    nome: 'Retirada de Porta — Via Fechadura / Trava',
    subtitulo: 'Técnica de expansão inicial na fechadura com abertura como alavanca',
    descricao:
      'Abertura direta pelo lado da fechadura (coluna B). Após criar folga com a Barra Halligan, o alargador expande no pino da trava até o destravamento mecânico, usando a própria porta aberta como alavanca.',
    peca: 'porta',
    configuracao: 'quatroRodas',
    complexidade: 'Intermediária',
    tempoEstimadoMinutos: 5,
    etapas: [
      {
        id: 'vidro',
        titulo: 'Manejo e quebra do vidro com Punção Holmatro',
        categoria: 'vidro',
        ferramenta: 'puncaoVidro',
        zonaAlvo: 'esquadria',
        requerManejoVidro: true,
        explicacao:
          'Ponta de carbeto do punção no canto inferior da janela, puxando e liberando a mola para quebra controlada antes da expansão da trava.',
        riscos: 'Ruptura descontrolada durante a expansão mecânica da fechadura.',
        dicaSeguranca: 'Posicionar a Proteção rígida (Lágrima) protegendo a vítima e manter comunicação constante.',
      },
      {
        id: 'gapTrava',
        titulo: 'Criação de gap na fechadura com Barra Halligan',
        categoria: 'abertura',
        ferramenta: 'hooligan',
        zonaAlvo: 'trava',
        operacaoPorta: 'criarAbertura',
        explicacao:
          'A ponta da Barra Halligan entra na fresta rente à fechadura da porta, encaixa e gira. O movimento de torção e alavanca cria o acesso inicial necessário para o alargador penetrar e romper o mecanismo da trava.',
        riscos: 'Deslizamento da ferramenta sobre a pintura.',
        dicaSeguranca: 'Insira a cunha com precisão entre o batente e a porta.',
      },
      {
        id: 'expandirTrava',
        titulo: 'Expansão no pino da trava com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'trava',
        operacaoPorta: 'expandirTrava',
        explicacao:
          'As pontas do alargador entram entre a porta e a coluna B na altura da trava. Ao expandir, a lingueta da fechadura é arrancada ou o pino é rompido, liberando a porta.',
        riscos: 'Estalo de alta intensidade e salto súbito da porta para fora.',
        dicaSeguranca: 'Posicione-se fora do curso de abertura da porta.',
      },
      {
        id: 'corteDobradicaSup',
        titulo: 'Estourar a dobradiça superior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaSuperior',
        operacaoPorta: 'expandirDobradiçaSuperior',
        explicacao:
          'Com a trava liberada e a porta parcialmente aberta, as pontas do alargador entram diretamente na dobradiça superior. Ao expandir, o pino e parafusos da dobradiça são estourados sob a força de separação.',
        riscos: 'Soltura súbita da dobradiça sob expansão mecânica.',
        dicaSeguranca: 'Mantenha a ferramenta equilibrada e apoio firme.',
      },
      {
        id: 'corteDobradicaInf',
        titulo: 'Estourar a dobradiça inferior com alargador',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'dobradicaInferior',
        operacaoPorta: 'expandirDobradiçaInferior',
        explicacao: 'O alargador estoura a dobradiça inferior com amplo espaço de manobra.',
        riscos: 'Descida rápida da porta.',
        dicaSeguranca: 'Comunicação clara entre os socorristas antes da liberação final.',
      },
      {
        id: 'sustentacao',
        titulo: 'Sustentação manual',
        categoria: 'sustentacao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'sustentarPorta',
        explicacao: 'Segurar a porta para descarte. Forçada além do curso, ela rompe o limitador; o chicote elétrico é desconectado à mão.',
        riscos: 'Cansaço muscular e perda de controle da carga.',
        dicaSeguranca: 'Dois socorristas participam da sustentação se a porta for blindada ou pesada.',
      },
      {
        id: 'remocao',
        titulo: 'Remoção concluída',
        categoria: 'remocao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoPorta: 'removerPorta',
        explicacao: 'Vão lateral totalmente exposto para o médico/resgate.',
        riscos: 'Tropeçamento nas ferramentas deixadas no solo.',
        dicaSeguranca: 'Mantenha a zona de trabalho limpa e organizada.',
      },
    ],
  },
  {
    id: 'terceiraPorta',
    nome: 'Técnica da 3ª Porta — Carroceria 2 Portas',
    subtitulo: 'Corte lateral em três lados para criar acesso aos ocupantes do banco traseiro',
    descricao:
      'Em veículos de duas portas com vítimas presas no banco de trás, realiza-se o corte da chapa lateral (três lados: vertical posterior, horizontal superior e horizontal inferior), dobrando a chapa como uma dobradiça para fora.',
    peca: 'terceiraPorta',
    configuracao: 'quatroRodas',
    complexidade: 'Avançada',
    tempoEstimadoMinutos: 6,
    etapas: [
      {
        id: 'corteVerticalTraseiro',
        titulo: 'Corte vertical posterior (junto à coluna C)',
        categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'traseiro',
        operacaoTerceiraPorta: 'cortarLadoTraseiro',
        explicacao:
          'Corte vertical da lataria traseira partindo da linha da janela até a soleira, seccionando os reforços externos da chapa lateral.',
        riscos: 'Contato com tubos de abastecimento de combustível ou fiações na lateral traseira.',
        dicaSeguranca: 'Inspeção prévia do lado do bocal de combustível antes de escolher o lado do corte.',
      },
      {
        id: 'corteSuperior',
        titulo: 'Corte horizontal superior (linha de cintura)',
        categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'superior',
        operacaoTerceiraPorta: 'cortarLadoSuperior',
        explicacao:
          'Corte horizontal ao longo da borda inferior da janela traseira, liberando a porção superior da chapa.',
        riscos: 'Fragmentos de metal e estilhaços de acabamento plástico interno.',
        dicaSeguranca: 'Proteja o ocupante traseiro com manta e escudo rígido.',
      },
      {
        id: 'corteInferior',
        titulo: 'Corte horizontal inferior (junto à soleira)',
        categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'inferior',
        operacaoTerceiraPorta: 'cortarLadoInferior',
        explicacao:
          'Corte rente à viga da soleira para liberar a base da nova porta. A borda dianteira (junto à coluna B) é preservada como dobradiça natural da peça.',
        riscos: 'Resistência elevada de aços multifásicos na soleira.',
        dicaSeguranca: 'Evite tentar cortar no ponto de maior espessura da solda por pontos.',
      },
      {
        id: 'sustentacao',
        titulo: 'Sustentação e alavancagem da aba',
        categoria: 'sustentacao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoTerceiraPorta: 'sustentarAba',
        explicacao:
          'Os socorristas seguram a aba cortada para orientar a dobra controlada para fora.',
        riscos: 'Corte acidental nas bordas afiadas da chapa recém-seccionada.',
        dicaSeguranca: 'Uso indispensável de luvas de resgate com proteção nível 5 contra corte.',
      },
      {
        id: 'rebatimentoFinal',
        titulo: 'Rebatimento total da 3ª porta',
        categoria: 'remocao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoTerceiraPorta: 'removerAba',
        explicacao:
          'A chapa é dobrada firmemente para fora contra a coluna dianteira, abrindo um amplo vão de 80 cm diretamente no banco traseiro para retirada da prancha.',
        riscos: 'Efeito mola da chapa dobrada retornando à posição original.',
        dicaSeguranca: 'Fixe a aba rebatida com fita catraca ou cunha de borracha.',
      },
    ],
  },
  {
    id: 'portaMalas',
    nome: 'Abertura Forçada da Tampa Traseira',
    subtitulo: 'Acesso posterior de emergência em capotamento ou colisões múltiplas',
    descricao:
      'Quando o veículo está sobre rodas ou tombado e as laterais estão severamente comprimidas, o porta-malas oferece uma via de extração em linha reta para a maca e alinhamento da coluna cervical.',
    peca: 'tampaTraseira',
    configuracao: 'quatroRodas',
    complexidade: 'Intermediária',
    tempoEstimadoMinutos: 4,
    etapas: [
      {
        id: 'gapTampa',
        titulo: 'Criação de abertura na fresta da fechadura',
        categoria: 'abertura',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'trava',
        operacaoTampa: 'criarAbertura',
        explicacao:
          'Inserção das pontas do alargador na fresta entre a tampa traseira e o para-choque na altura da fechadura.',
        riscos: 'Deformação do painel traseiro sem liberação da trava.',
        dicaSeguranca: 'Aplique as pontas o mais próximo possível do mecanismo da fechadura.',
      },
      {
        id: 'expandirTrava',
        titulo: 'Expansão e destravamento da lingueta',
        categoria: 'rompimento',
        ferramenta: 'alargador',
        modoAlargador: 'abrir',
        zonaAlvo: 'trava',
        operacaoTampa: 'expandirTrava',
        explicacao:
          'Abertura do alargador até que a trava mecânica seja superada e a tampa salte do batente.',
        riscos: 'Movimento brusco de subida da tampa impulsionada pelo pistão a gás.',
        dicaSeguranca: 'Mantenha a mão superior na borda da tampa para controlar a elevação.',
      },
      {
        id: 'liberarApoioGas',
        titulo: 'Corte do amortecedor / pistão a gás',
        categoria: 'vinculos',
        ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'apoio',
        operacaoTampa: 'liberarApoio',
        explicacao:
          'Seccionamento da haste do pistão a gás do porta-malas para evitar que a pressão interna mantenha a tampa em posição rígida descontrolada.',
        riscos: 'Vazamento sob pressão de óleo hidráulico ou gás nitrogênio do amortecedor.',
        dicaSeguranca: 'Posicione o cortador na haste cilíndrica e nunca no corpo pressurizado do pistão.',
      },
      {
        id: 'cortarDobradicas',
        titulo: 'Corte das dobradiças superiores da tampa',
        categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'dobradicaEsquerda',
        operacaoTampa: 'cortarDobradicaEsquerda',
        explicacao:
          'Corte das dobradiças no teto traseiro para permitir a remoção completa da tampa do porta-malas.',
        riscos: 'Queda da tampa sobre o cofre traseiro.',
        dicaSeguranca: 'Sustente a tampa antes de efetuar o corte da última dobradiça.',
      },
      {
        id: 'cortarDobradiçaDireita', titulo: 'Corte da dobradiça direita', categoria: 'rompimento', ferramenta: 'cortadorHidraulico',
        zonaAlvo: 'dobradicaDireita', operacaoTampa: 'cortarDobradicaDireita',
        explicacao: 'A segunda dobradiça é liberada para concluir a separação da tampa.',
        riscos: 'Queda súbita da tampa.', dicaSeguranca: 'A equipe deve sustentar a tampa antes deste corte.',
      },
      {
        id: 'sustentarTampa', titulo: 'Sustentação da tampa', categoria: 'sustentacao', ferramenta: 'nenhuma', zonaAlvo: null,
        operacaoTampa: 'sustentarTampa', explicacao: 'A equipe recebe o peso da tampa antes da retirada.',
        riscos: 'Perda de controle da peça.', dicaSeguranca: 'Coordenar a sustentação antes de mover a tampa.',
      },
      {
        id: 'remocaoTampa',
        titulo: 'Remoção completa e liberação da rota reta',
        categoria: 'remocao',
        ferramenta: 'nenhuma',
        zonaAlvo: null,
        operacaoTampa: 'removerTampa',
        explicacao:
          'Retirada da tampa traseira, liberando uma rota perfeitamente alinhada de 0° para extração da vítima no banco traseiro ou condutor com bancos rebatidos.',
        riscos: 'Tropeçamento nas ferragens.',
        dicaSeguranca: 'Leve a tampa imediatamente para a área de descarte da cena.',
      },
    ],
  },
]


const tecnicaTrava = TECNICAS_BASE.find((t) => t.id === 'portaTrava')!
const TECNICAS_EXISTENTES: readonly TecnicaCorte[] = [
  ...TECNICAS_BASE.slice(0, 3),
  retiradaViaAcesso(tecnicaTrava, 'portaMacaneta', {
    id: 'comprimirMacaneta', titulo: 'Comprimir a maçaneta e criar acesso', categoria: 'abertura',
    ferramenta: 'alargador', modoAlargador: 'fechar', zonaAlvo: 'macaneta', operacaoPorta: 'comprimirMacaneta',
    explicacao: 'Após o manejo do vidro, as pontas do alargador fecham sobre a maçaneta da porta. A compressão deforma essa região e cria acesso para posicionar a ferramenta no pino da trava.',
    riscos: 'Escorregamento das pontas e deslocamento da chapa sob compressão.',
    dicaSeguranca: 'Mantenha a proteção da vítima e as mãos fora das pontas; coordene a sustentação da porta.',
  }),
  retiradaViaAcesso(tecnicaTrava, 'portaEsquadria', {
    id: 'abrirEsquadria', titulo: 'Abrir entre a esquadria superior e a inferior', categoria: 'abertura',
    ferramenta: 'alargador', modoAlargador: 'abrir', zonaAlvo: 'esquadria', operacaoPorta: 'abrirPelaJanela',
    explicacao: 'Após o manejo do vidro, posicione uma ponta do alargador na esquadria superior e a outra na esquadria inferior da mesma porta. Ao abrir, a moldura se deforma e cria acesso ao pino da trava.',
    riscos: 'Perda de apoio das pontas e movimento da moldura durante a expansão.',
    dicaSeguranca: 'Observe os dois apoios e mantenha a proteção da vítima durante a criação do acesso.',
  }),
  ...TECNICAS_BASE.slice(3),
]


const acessoPorta = TECNICAS_EXISTENTES.find((t) => t.id === 'portaMacaneta')!
export const CATALOGO_TECNICAS_CORTE: readonly TecnicaCorte[] = [
  ...TECNICAS_EXISTENTES,
  {
    id: 'rebatimentoPainel', nome: 'Rebatimento de Painel',
    subtitulo: 'Cortes na coluna A e extensor apoiado na base da coluna B',
    descricao: 'Retire a porta, faça o corte médio e o corte de alívio na base da coluna A, instale o apoio na base da coluna B e posicione o extensor na região cortada. O acionamento rebate o painel e cria espaço para liberar o aprisionamento da vítima.',
    peca: 'porta', configuracao: 'quatroRodas', complexidade: 'Avançada', tempoEstimadoMinutos: 8,
    etapas: [
      ...acessoPorta.etapas.map((etapa) => ({ ...etapa, id: `porta.${etapa.id}` })),
      {
        id: 'corteMedioA', titulo: 'Corte na região média da coluna A', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'colunaAMedia', operacaoPainel: 'cortarColunaAMedia',
        explicacao: 'Com a porta já retirada, posicione o cortador na região média da coluna A e realize o corte mostrado na demonstração.',
        riscos: 'Bordas cortantes e deslocamento do segmento da coluna.',
        dicaSeguranca: 'Mantenha a proteção da vítima e o controle da peça durante o corte.',
      },
      {
        id: 'alivioA', titulo: 'Corte de alívio na base da coluna A', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'colunaABase', operacaoPainel: 'cortarAlivioColunaA',
        explicacao: 'Faça o segundo corte na base da coluna A. Esse corte de alívio permite o movimento do segmento durante o rebatimento.',
        riscos: 'Tensão residual na estrutura e bordas expostas.',
        dicaSeguranca: 'Observe a estabilidade e mantenha a proteção entre o ponto de trabalho e a vítima.',
      },
      {
        id: 'apoioB', titulo: 'Instalar o apoio do extensor na base da coluna B', categoria: 'sustentacao',
        ferramenta: 'nenhuma', zonaAlvo: 'baseExtensor', operacaoPainel: 'instalarApoioExtensor',
        explicacao: 'Posicione o apoio do extensor na soleira, junto à base da coluna B, como na referência do instrutor. Ele recebe a base da ferramenta.',
        riscos: 'Deslizamento do apoio quando a ferramenta recebe carga.',
        dicaSeguranca: 'Confira o assentamento do apoio antes de posicionar e acionar o extensor.',
      },
      {
        id: 'posicionarRam', titulo: 'Posicionar o extensor entre o apoio e a coluna A cortada', categoria: 'abertura',
        ferramenta: 'extensor', zonaAlvo: 'apoioColunaA', operacaoPainel: 'posicionarExtensor',
        explicacao: 'A base do extensor fica no apoio instalado junto à coluna B; a cabeça encosta na região cortada da coluna A. Confira os dois pontos antes do acionamento.',
        riscos: 'Perda de apoio da base ou da cabeça da ferramenta.',
        dicaSeguranca: 'Mantenha os dois pontos de contato alinhados e as mãos fora da linha de força.',
        animacao: { executandoMs: 1200 },
      },
      {
        id: 'acionarRam', titulo: 'Acionar o extensor e rebater o painel', categoria: 'abertura',
        ferramenta: 'extensor', zonaAlvo: 'apoioColunaA', operacaoPainel: 'acionarExtensor',
        explicacao: 'Acione o extensor. A base permanece apoiada na coluna B enquanto a cabeça desloca a região cortada da coluna A e o painel para a dianteira, ampliando o espaço junto às pernas da vítima.',
        riscos: 'Deslocamento estrutural durante a aplicação de força.',
        dicaSeguranca: 'Acompanhe a vítima, os apoios e a estabilização enquanto o painel se move.',
        animacao: { executandoMs: 3500, observandoMs: 1400 },
      },
      {
        id: 'liberarVitima', titulo: 'Liberar o aprisionamento da vítima', categoria: 'remocao',
        ferramenta: 'nenhuma', zonaAlvo: 'apoioColunaA', operacaoPainel: 'liberarVitima',
        explicacao: 'Confira o espaço criado e a liberação do aprisionamento. A demonstração termina com a vítima liberada para a etapa de extração.',
        riscos: 'Contato com bordas cortantes na rota de saída.',
        dicaSeguranca: 'Proteja as bordas e coordene a extração após confirmar que a vítima está livre.',
      },
    ],
  },
  {
    id: 'elevacaoPainel', nome: 'Elevação de Painel',
    subtitulo: 'Dois cortes na base A, dobra da aba e elevação com alargador',
    descricao: 'Retire a porta, corte a coluna A e faça dois cortes na sua base. Dobre a parte recortada com o alargador para criar o espaço de apoio; reposicione as pontas e abra a ferramenta para elevar o painel.',
    peca: 'porta', configuracao: 'quatroRodas', complexidade: 'Avançada', tempoEstimadoMinutos: 8,
    etapas: [
      ...acessoPorta.etapas.map((etapa) => ({ ...etapa, id: `porta.${etapa.id}` })),
      {
        id: 'corteA', titulo: 'Cortar a coluna A', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'corteAElevacao', operacaoElevacao: 'cortarColunaA',
        explicacao: 'Com a porta retirada, realize o corte na coluna A mostrado na demonstração, separando o segmento que acompanha o painel do trecho superior.',
        riscos: 'Bordas cortantes e deslocamento do segmento da coluna.',
        dicaSeguranca: 'Mantenha a proteção da vítima e o controle da peça durante o corte.',
      },
      {
        id: 'baseASuperior', titulo: 'Primeiro corte na base da coluna A', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'corteBaseASuperior', operacaoElevacao: 'cortarBaseSuperior',
        explicacao: 'Faça o corte superior na base da coluna A. Ele delimita a parte que será dobrada para abrir o espaço de apoio do alargador.',
        riscos: 'Tensão residual na estrutura e bordas expostas.',
        dicaSeguranca: 'Observe a estabilidade e proteja a vítima durante o corte.',
      },
      {
        id: 'baseAInferior', titulo: 'Segundo corte na base da coluna A', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'corteBaseAInferior', operacaoElevacao: 'cortarBaseInferior',
        explicacao: 'Faça o segundo corte abaixo do primeiro, delimitando a aba entre os dois cortes na base A, conforme a sequência do instrutor.',
        riscos: 'Bordas cortantes no segmento recortado.',
        dicaSeguranca: 'Mantenha as mãos fora da região recortada e acompanhe a vítima.',
      },
      {
        id: 'dobrarAba', titulo: 'Dobrar a parte recortada com o alargador', categoria: 'abertura',
        ferramenta: 'alargador', modoAlargador: 'fechar', zonaAlvo: 'abaElevacao', operacaoElevacao: 'dobrarAba',
        explicacao: 'O alargador pinça a parte delimitada pelos dois cortes e a dobra para fora. A abertura resultante cria o espaço para apoiar a ponta da ferramenta.',
        riscos: 'Deslocamento da chapa e perda de contato das pontas.',
        dicaSeguranca: 'Mantenha a proteção da vítima e acompanhe a dobra da chapa.',
        animacao: { executandoMs: 2500, observandoMs: 1400 },
      },
      {
        id: 'apoioAlargador', titulo: 'Apoiar as pontas do alargador no espaço criado', categoria: 'sustentacao',
        ferramenta: 'alargador', modoAlargador: 'abrir', zonaAlvo: 'apoioAlargadorPainel', operacaoElevacao: 'posicionarAlargador',
        explicacao: 'Reposicione o alargador no espaço criado pela dobra: uma ponta permanece no apoio inferior e a outra recebe a região móvel junto ao painel.',
        riscos: 'Perda de apoio de uma das pontas durante a abertura.',
        dicaSeguranca: 'Confira os dois contatos antes de acionar a ferramenta.',
        animacao: { executandoMs: 1200 },
      },
      {
        id: 'elevarPainel', titulo: 'Acionar o alargador e elevar o painel', categoria: 'abertura',
        ferramenta: 'alargador', modoAlargador: 'abrir', zonaAlvo: 'apoioAlargadorPainel', operacaoElevacao: 'elevarPainel',
        explicacao: 'Ao abrir o alargador, a ponta inferior mantém seu apoio enquanto a superior eleva a região cortada da coluna A e o painel, ampliando o espaço junto às pernas.',
        riscos: 'Movimento da estrutura durante o acionamento.',
        dicaSeguranca: 'Acompanhe a vítima, os dois apoios e a estabilização enquanto o painel sobe.',
        animacao: { executandoMs: 3500, observandoMs: 1600 },
      },
    ],
  },
  {
    id: 'rebatimentoLateralTeto', nome: 'Rebatimento Lateral de Teto',
    subtitulo: 'Carro tombado: colunas superiores, para-brisa e alívios inferiores',
    descricao: 'Com o veículo tombado, corte as colunas A, B, C e D do lado superior. Use a machadinha para criar acesso e a serra sabre para cortar o para-brisa. Faça dois cortes de alívio no teto junto às colunas A e D do lado inferior e controle o rebatimento lateral.',
    peca: 'porta', configuracao: 'tombado', complexidade: 'Avançada', tempoEstimadoMinutos: 8,
    etapas: [
      ...(['A', 'B', 'C', 'D'] as const).map((coluna): EtapaDemonstracaoCorte => ({
        id: `corte${coluna}`, titulo: `Cortar a coluna ${coluna} do lado superior`, categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: `tetoColuna${coluna}`, operacaoTetoLateral: `cortar${coluna}`,
        explicacao: `Com o carro tombado e estabilizado, o cortador secciona a coluna ${coluna} do lado que está para cima. O segmento ligado ao teto permanece sustentado durante a preparação.`,
        riscos: 'Movimento da estrutura e bordas cortantes após o corte.',
        dicaSeguranca: 'Mantenha o controle do teto e a proteção da vítima durante o corte.',
      })),
      {
        id: 'acessoVidro', titulo: 'Criar acesso no para-brisa com machadinha', categoria: 'vidro',
        ferramenta: 'machadinha', zonaAlvo: 'tetoParabrisa', operacaoTetoLateral: 'abrirAcessoVidro',
        explicacao: 'A machadinha cria o acesso inicial no vidro dianteiro para a entrada da lâmina. A demonstração usa depois a serra sabre para percorrer a linha do corte.',
        riscos: 'Projeção de fragmentos e contato com a lâmina da machadinha.',
        dicaSeguranca: 'Proteja a vítima e a equipe do vidro antes de iniciar o acesso.',
      },
      {
        id: 'corteParabrisa', titulo: 'Cortar o vidro dianteiro com serra sabre', categoria: 'vidro',
        ferramenta: 'serraSabre', zonaAlvo: 'tetoParabrisa', operacaoTetoLateral: 'cortarParabrisa',
        explicacao: 'A serra sabre entra no acesso criado e percorre a linha do para-brisa, separando o vínculo dianteiro antes dos cortes de alívio do teto.',
        riscos: 'Pó de vidro e projeção de partículas durante o corte.',
        dicaSeguranca: 'Mantenha a vítima protegida e acompanhe o percurso da lâmina.',
        animacao: { executandoMs: 4000, observandoMs: 1300 },
      },
      {
        id: 'alivioAInferior', titulo: 'Corte de alívio no teto junto à coluna A inferior', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'tetoAlivioA', operacaoTetoLateral: 'alivioA',
        explicacao: 'Faça um corte de alívio horizontal no teto, entrando pela extremidade dianteira junto à coluna A que está para baixo. Mantenha as mandíbulas no plano horizontal. Esse ponto é diferente da coluna A superior já seccionada.',
        riscos: 'Bordas cortantes e movimento do teto durante o alívio.',
        dicaSeguranca: 'Controle a peça e mantenha as mãos fora da linha de corte.',
      },
      {
        id: 'alivioDInferior', titulo: 'Corte de alívio no teto junto à coluna D inferior', categoria: 'rompimento',
        ferramenta: 'cortadorHidraulico', zonaAlvo: 'tetoAlivioD', operacaoTetoLateral: 'alivioD',
        explicacao: 'Faça o segundo corte de alívio horizontal no teto, entrando pela extremidade traseira junto à coluna D do lado inferior. Os dois alívios ficam nas extremidades do lado que apoia o rebatimento.',
        riscos: 'Soltura ou deslocamento súbito da estrutura após o último alívio.',
        dicaSeguranca: 'Coordene a sustentação do teto antes de concluir o segundo alívio.',
      },
      {
        id: 'rebaterTeto', titulo: 'Rebater o teto lateralmente sob controle', categoria: 'remocao',
        ferramenta: 'nenhuma', zonaAlvo: 'tetoRebater', operacaoTetoLateral: 'rebaterTeto',
        explicacao: 'Com as colunas superiores e o para-brisa cortados e os dois alívios concluídos, o teto rebate para fora em torno da borda inferior, ampliando o vão de acesso.',
        riscos: 'Queda da peça ou contato com bordas cortantes.',
        dicaSeguranca: 'Mantenha o movimento sustentado, acompanhe a vítima e proteja as bordas do vão.',
        animacao: { executandoMs: 3500, observandoMs: 1700 },
      },
    ],
  },
]

export function tecnicaCortePorId(id: IdTecnicaCorte): TecnicaCorte {
  const encontrada = CATALOGO_TECNICAS_CORTE.find((t) => t.id === id)
  if (!encontrada) {
    throw new Error(`Técnica de corte não encontrada: ${id}`)
  }
  return encontrada
}
